import { useState } from 'react'
import { createCommand } from '@/api/nodes'
import type { CommandType, CreateCommandRequest, RecordedFile } from '@/types'
import RecordStartForm from './RecordStartForm'
import UploadStartForm from './UploadStartForm'

// Re-export for consumers that imported CommandType from this module
export type { CommandType }

// Only the 4 action types the modal handles directly
export type ModalCommandType =
  | 'recording.start'
  | 'recording.stop'
  | 'upload.start'
  | 'upload.stop'

interface Props {
  nodeId: string
  commandType: ModalCommandType
  files?: RecordedFile[]
  uploadSourceKind?: 'recording' | 'inference_recording'
  initialUploadFileIds?: string[]
  canDeleteLocal?: boolean
  initialCameraKey?: string
  onClose: () => void
  onSuccess: (message: string) => void
}

const TITLES: Record<ModalCommandType, string> = {
  'recording.start': 'Start Recording',
  'recording.stop': 'Stop Recording',
  'upload.start': 'Start Upload',
  'upload.stop': 'Stop Upload',
}

function generateIdempotencyKey(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

export default function CommandModal({
  nodeId,
  commandType,
  files = [],
  uploadSourceKind = 'recording',
  initialUploadFileIds = [],
  canDeleteLocal = false,
  initialCameraKey,
  onClose,
  onSuccess,
}: Props) {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<{ message: string; conflict: boolean } | null>(null)

  async function submit(req: CreateCommandRequest) {
    setSubmitting(true)
    setError(null)
    const idempotencyKey = req.idempotency_key ?? generateIdempotencyKey()
    try {
      const res = await createCommand(nodeId, { ...req, idempotency_key: idempotencyKey })
      if (res.status === 'queued' || res.status === 'running') {
        onSuccess('Command queued successfully')
      } else {
        onSuccess('Command was already in progress — deduplicated')
      }
    } catch (err: unknown) {
      const e = err as { status?: number; message?: string }
      if (e?.status === 409) {
        setError({ message: e.message ?? 'Conflicting command already running', conflict: true })
      } else {
        setError({ message: e?.message ?? 'Failed to create command', conflict: false })
      }
    } finally {
      setSubmitting(false)
    }
  }

  const isStopCommand = commandType === 'recording.stop' || commandType === 'upload.stop'

  return (
    <div style={backdropStyle}>
      <div style={modalStyle} role="dialog" aria-modal="true">
        <div style={headerStyle}>
          <span style={{ fontWeight: 700, fontSize: 15 }}>
            {commandType === 'upload.start' && uploadSourceKind === 'inference_recording'
              ? 'Start Inference Recording Upload'
              : TITLES[commandType]}
          </span>
          <button onClick={onClose} style={closeBtn} aria-label="Close">x</button>
        </div>

        {error && (
          <div
            style={{
              background: error.conflict ? '#fff8e1' : '#ffebee',
              border: `1px solid ${error.conflict ? '#ffe082' : '#ffcdd2'}`,
              borderRadius: 4,
              padding: '10px 12px',
              marginBottom: 14,
              fontSize: 13,
              color: error.conflict ? '#b71c1c' : '#c62828',
            }}
          >
            {error.message}
            {error.conflict && (
              <div style={{ marginTop: 6, color: '#795548', fontSize: 12 }}>
                Stop or cancel the conflicting work, then try again.
              </div>
            )}
          </div>
        )}

        {isStopCommand ? (
          <div>
            <p style={{ fontSize: 14, color: '#444', marginBottom: 16 }}>
              {commandType === 'recording.stop'
                ? `Stop the recording${initialCameraKey ? ` for ${initialCameraKey}` : ''}?`
                : 'Stop the currently running upload on this node?'}
            </p>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={cancelBtnStyle} disabled={submitting}>
                Cancel
              </button>
              <button
                onClick={() =>
                  submit({
                    type: commandType,
                    params: commandType === 'recording.stop' && initialCameraKey
                      ? { camera_key: initialCameraKey }
                      : undefined,
                    idempotency_key: generateIdempotencyKey(),
                  })
                }
                style={{
                  ...submitBtnStyle,
                  background: '#c62828',
                }}
                disabled={submitting}
              >
                {submitting ? 'Sending...' : 'Confirm stop'}
              </button>
            </div>
          </div>
        ) : commandType === 'recording.start' ? (
          <RecordStartForm
            nodeId={nodeId}
            onSubmit={submit}
            onCancel={onClose}
            submitting={submitting}
            initialCameraKey={initialCameraKey}
          />
        ) : (
          <UploadStartForm
            files={files}
          sourceKind={uploadSourceKind}
          initialSelectedFileIds={initialUploadFileIds}
          canDeleteLocal={canDeleteLocal}
            onSubmit={submit}
            onCancel={onClose}
            submitting={submitting}
          />
        )}
      </div>
    </div>
  )
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.4)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
}

const modalStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: 8,
  padding: '20px 24px',
  width: 480,
  maxWidth: '95vw',
  maxHeight: '90vh',
  overflowY: 'auto',
  boxShadow: '0 8px 32px rgba(0,0,0,0.18)',
}

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 18,
  paddingBottom: 12,
  borderBottom: '1px solid #e0e0e0',
}

const closeBtn: React.CSSProperties = {
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  fontSize: 16,
  color: '#888',
  padding: '2px 6px',
  fontFamily: 'inherit',
}

const cancelBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#fff',
  border: '1px solid #ccc',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}

const submitBtnStyle: React.CSSProperties = {
  padding: '7px 16px',
  background: '#3949ab',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  fontSize: 13,
  cursor: 'pointer',
  fontFamily: 'inherit',
}
